import { useCallback, useEffect, useRef, useState } from 'react'
import Http from '../lib/http'
import { responseToItem } from '../views/Notifications/notifications.adapter'
import { FOREGROUND_REFETCH_DELAY_MS, subscribeToForegroundMessages, WEB_PUSH_ENABLED_EVENT } from '../services/firebaseMessaging'
import { INBOX_NOTIFICATIONS_CHANGED_EVENT } from '../views/Notifications/inboxNotifications.events'

const UNREAD_COUNT_URL = '/api/inbox/notifications/unread-count'
const LIST_URL = '/api/inbox/notifications'
const POLL_INTERVAL_MS = 60_000

export function useInboxNotifications() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState([])
  const [isFetchingList, setIsFetchingList] = useState(false)
  const intervalRef = useRef(null)
  const mountedRef = useRef(true)
  const fcmDebounceRef = useRef(null)

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await Http.get(UNREAD_COUNT_URL)
      if (!mountedRef.current) return
      const count = res?.data?.data?.unread_count ?? 0
      setUnreadCount(Number(count))
    } catch (_) {
      // silently ignore — badge simply stays stale
    }
  }, [])

  const fetchList = useCallback(async () => {
    try {
      setIsFetchingList(true)
      const res = await Http.get(LIST_URL, { params: { page: 1, per_page: 10, unread: 1 } })
      if (!mountedRef.current) return
      const rows = res?.data?.data ?? []
      setItems(Array.isArray(rows) ? rows.map(responseToItem) : [])
      const metaCount = res?.data?.meta?.unread_count
      if (metaCount !== undefined) setUnreadCount(Number(metaCount))
    } catch (_) {
      // leave previous items displayed
    } finally {
      if (mountedRef.current) setIsFetchingList(false)
    }
  }, [])

  const markOneRead = useCallback(
    async (id) => {
      // optimistic
      setItems((prev) => prev.filter((n) => n.id !== id))
      setUnreadCount((prev) => Math.max(0, prev - 1))
      try {
        await Http.post(`/api/inbox/notifications/${id}/read`)
      } catch (_) {
        // revert on failure — re-fetch is the safest recovery
        fetchList()
        fetchUnreadCount()
      }
    },
    [fetchList, fetchUnreadCount],
  )

  const markAllRead = useCallback(async () => {
    // optimistic
    setItems([])
    setUnreadCount(0)
    try {
      await Http.post('/api/inbox/notifications/read-all')
    } catch (_) {
      fetchList()
      fetchUnreadCount()
    }
  }, [fetchList, fetchUnreadCount])

  // Start / restart the polling interval — only while tab is visible
  const startPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnreadCount()
    }, POLL_INTERVAL_MS)
  }, [fetchUnreadCount])

  useEffect(() => {
    mountedRef.current = true

    // Initial fetch
    fetchUnreadCount()

    // Start polling
    startPolling()

    // Refetch immediately when the tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    const refetchFromFcm = () => {
      if (!mountedRef.current) return
      if (fcmDebounceRef.current) clearTimeout(fcmDebounceRef.current)
      fcmDebounceRef.current = setTimeout(() => {
        if (!mountedRef.current) return
        fetchUnreadCount()
        fetchList()
      }, FOREGROUND_REFETCH_DELAY_MS)
    }

    let unsubscribeFcm = () => {}
    subscribeToForegroundMessages(refetchFromFcm).then((unsub) => {
      unsubscribeFcm = unsub ?? (() => {})
    })

    const handleWebPushEnabled = () => refetchFromFcm()
    window.addEventListener(WEB_PUSH_ENABLED_EVENT, handleWebPushEnabled)

    const handleInboxChanged = () => fetchUnreadCount()
    window.addEventListener(INBOX_NOTIFICATIONS_CHANGED_EVENT, handleInboxChanged)

    return () => {
      mountedRef.current = false
      clearInterval(intervalRef.current)
      if (fcmDebounceRef.current) clearTimeout(fcmDebounceRef.current)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener(WEB_PUSH_ENABLED_EVENT, handleWebPushEnabled)
      window.removeEventListener(INBOX_NOTIFICATIONS_CHANGED_EVENT, handleInboxChanged)
      unsubscribeFcm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { unreadCount, items, isFetchingList, fetchList, markOneRead, markAllRead }
}
